/*
 * ALORI — cart "subscribe & save" upsell behaviour.
 * Markup: snippets/cart-subscribe-upsell.liquid (rendered inside the cart drawer).
 * Loaded from snippets/alori-body-end.liquid (moved out of the old drawer-cart
 * snippet so theme updates no longer delete it).
 */
(function () {
  'use strict';

  /* ─── State ─────────────────────────────────────────────────────────────────────────── */
  var _upgradeItems       = [];
  var _checkTimer         = null;
  var _widgetReady        = false;
  var _cartItemsObserver  = null;   /* watches [data-cart-items] for DOM changes */

  /* ─── DOM helpers ─────────────────────────────────────────────────────────────────────── */
  function getWidget() {
    return document.getElementById('cart-sub-upsell');
  }

  function setVisible(show) {
    var w = getWidget();
    if (!w) return;
    if (show) {
      w.removeAttribute('hidden');
    } else {
      w.setAttribute('hidden', '');
      _upgradeItems = [];
    }
  }

  /* ─── MutationObserver: hide upsell when cart items section changes ──────────── */
  /*
   * The Shapes theme does not always dispatch a named event when a cart item
   * is removed — it just replaces the [data-cart-items] section content via
   * AJAX. Watching the DOM directly is the most reliable way to catch this.
   * The observer is scoped to [data-cart-items] so it’s cheap; it does NOT
   * fire when setVisible() mutates the upsell widget, so no infinite loop.
   */
  function attachCartItemsObserver() {
    if (_cartItemsObserver) {
      _cartItemsObserver.disconnect();
      _cartItemsObserver = null;
    }
    var island = document.querySelector('[data-cart-items]');
    if (!island) return;
    _cartItemsObserver = new MutationObserver(function () {
      scheduleCheck(250);
    });
    _cartItemsObserver.observe(island, { childList: true, subtree: true, characterData: true });
  }

  /* ─── In-place drawer refresh (NO page reload) ────────────────────────────────── */
  /* Uses the shared ALORI cart helper (assets/alori-cart.js), which asks the
     theme which sections to re-render and fires the theme's own update event.
     Works on Shapes 4.x and 5.x without changes here. */
  function refreshDrawer() {
    var p = window.AloriCart ? window.AloriCart.refresh() : Promise.resolve();
    p.then(function () { scheduleCheck(400); }, function () { scheduleCheck(400); });
  }

  /* ─── Cart check ─────────────────────────────────────────────────────────────────────── */
  function checkCart() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {

        /* One-time items = no selling plan yet */
        var oneTime = (cart.items || []).filter(function (i) {
          return !i.selling_plan_allocation;
        });
        if (!oneTime.length) { setVisible(false); return; }

        /* Deduplicate handles */
        var seen = {}, handles = [];
        oneTime.forEach(function (item) {
          if (!seen[item.handle]) {
            seen[item.handle] = true;
            handles.push(item.handle);
          }
        });

        /* Fetch all products in parallel */
        return Promise.all(
          handles.map(function (h) {
            return fetch('/products/' + h + '.js', {
              headers: { Accept: 'application/json' }
            }).then(function (r) { return r.json(); });
          })
        ).then(function (products) {

          /* Build handle → best Seal plan map */
          var planByHandle = {};
          products.forEach(function (product) {
            var bestDiscount = 0, bestPlanId = null;
            (product.selling_plan_groups || []).forEach(function (grp) {
              (grp.selling_plans || []).forEach(function (plan) {
                (plan.price_adjustments || []).forEach(function (adj) {
                  if (adj.value_type === 'percentage' && adj.value > bestDiscount) {
                    bestDiscount = adj.value;
                    bestPlanId   = plan.id;
                  }
                });
              });
            });
            planByHandle[product.handle] = {
              bestDiscount: bestDiscount,
              bestPlanId:   bestPlanId
            };
          });

          /* Only items that actually have a plan available */
          var upgradeable = oneTime.filter(function (item) {
            var p = planByHandle[item.handle];
            return p && p.bestPlanId;
          });
          if (!upgradeable.length) { setVisible(false); return; }

          /* Best discount across all upgradeable items */
          var best = 0;
          upgradeable.forEach(function (item) {
            var d = planByHandle[item.handle].bestDiscount;
            if (d > best) best = d;
          });

          /* Store full item info (variantId + quantity needed for fallback) */
          _upgradeItems = upgradeable.map(function (item) {
            return {
              key:       item.key,
              planId:    planByHandle[item.handle].bestPlanId,
              variantId: item.variant_id,
              quantity:  item.quantity
            };
          });

          var pct = document.getElementById('cart-sub-upsell-pct');
          if (pct) pct.textContent = best;
          setVisible(true);
        });
      })
      .catch(function () {});
  }

  /* Debounce */
  function scheduleCheck(delay) {
    clearTimeout(_checkTimer);
    _checkTimer = setTimeout(checkCart, delay == null ? 100 : delay);
  }

  /* ─── Upgrade button ─────────────────────────────────────────────────────────────────────── */
  function handleUpgrade() {
    if (!_upgradeItems.length) return;
    var btn = document.getElementById('cart-sub-upsell-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Upgrading…'; }

    Promise.all(_upgradeItems.map(function (item) {
      return fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body:    JSON.stringify({ id: item.key, selling_plan: item.planId })
      })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status && data.status >= 400) {
          /* Fallback: remove then re-add with selling plan */
          return fetch('/cart/change.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body:    JSON.stringify({ id: item.key, quantity: 0 })
          })
          .then(function () {
            return fetch('/cart/add.js', {
              method:  'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body:    JSON.stringify({
                id:           item.variantId,
                quantity:     item.quantity,
                selling_plan: item.planId
              })
            }).then(function (r) { return r.json(); });
          });
        }
        return data;
      });
    }))
    .then(function () {
      setVisible(false);
      refreshDrawer();
    })
    .catch(function () {
      if (btn) { btn.disabled = false; btn.textContent = 'UPGRADE & SAVE'; }
    });
  }

  /* ─── Widget init ─────────────────────────────────────────────────────────────────────────── */
  function onWidgetLanded() {
    if (_widgetReady) return;
    _widgetReady = true;
    var w = getWidget();
    if (!w) return;
    var btn = w.querySelector('.cart-sub-upsell__btn');
    if (btn && !btn.dataset.subInit) {
      btn.dataset.subInit = '1';
      btn.addEventListener('click', handleUpgrade);
    }
    /* Start observing [data-cart-items] for any DOM changes */
    attachCartItemsObserver();
    checkCart();
  }

  /* ─── Widget detection ────────────────────────────────────────────────────────────────────── */
  function waitForWidget() {
    if (getWidget()) { onWidgetLanded(); return; }

    var obs = new MutationObserver(function (_, self) {
      if (getWidget()) { self.disconnect(); clearInterval(poll); onWidgetLanded(); }
    });
    obs.observe(document.body, { childList: true, subtree: true });

    var ticks = 0;
    var poll = setInterval(function () {
      ticks++;
      if (_widgetReady || ticks > 30) { clearInterval(poll); obs.disconnect(); return; }
      if (getWidget()) { clearInterval(poll); obs.disconnect(); onWidgetLanded(); }
    }, 400);
  }

  /* ─── Event hooks ─────────────────────────────────────────────────────────────────────── */

  /* Shapes theme + Seal cart events on document */
  ['cart:refresh', 'cart-update', 'theme:cart:refresh', 'cart:updated'].forEach(
    function (n) { document.addEventListener(n, function () { scheduleCheck(150); }); }
  );

  /* Shapes-specific events dispatched on window (e.g. after ATC via sticky bar) */
  ['shapes:cart:afteradditem', 'shapes:cart:cartqtychange', 'shapes:cart:update', 'shapes:cart:afterremoveitem', 'cart:refresh', 'cart:updated'].forEach(
    function (n) { window.addEventListener(n, function () { scheduleCheck(200); }); }
  );

  /* ATC button clicks */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[name="add"], .sticky-add-btn, [data-atc-btn], [data-add-to-cart]')) {
      scheduleCheck(1500);
      setTimeout(function () { scheduleCheck(0); }, 3500);
    }
  });

  /* Cart icon / links that open the drawer */
  document.addEventListener('click', function (e) {
    if (e.target.closest(
      '[data-open-cart], [data-modal-open="cart"], .cart-count-bubble, [href="/cart"], [data-cart-icon-bubble]'
    )) {
      scheduleCheck(350);
    }
  });

  /* Seal Subscriptions event */
  document.addEventListener('SealSubscriptions:cartUpdated', function () { scheduleCheck(600); });

  /* Alpine store watcher — fires every time the right drawer switches to cart */
  document.addEventListener('alpine:initialized', function () {
    try {
      Alpine.effect(function () {
        if (Alpine.store('modals').rightDrawer.contents === 'cart') {
          scheduleCheck(300);
        }
      });
    } catch (_) {}
  });

  /* Boot */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForWidget);
  } else {
    waitForWidget();
  }

}());
